import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Confetti from 'react-confetti';
import { useNavigate } from 'react-router-dom';
import Grid from '../../Grid';
import Keyboard from '../../Keyboard';
import GameImage from '../unlimited/GameImage';
import DailyCountdown from './DailyCountdown';
import DailyGameModal from './DailyGameModal';
import DailyHowToPlayModal from './DailyHowToPlayModal';
import DailyRiddleHint from './DailyRiddleHint';
import ImagePreviewModal from '../shared/ImagePreviewModal';
import { useProfile } from '../../../contexts/ProfileContext';
import { useDebouncedCallback } from '../../../utils/hooks/useDebouncedCallback';
import { fetchDailyPuzzle } from '../../../utils/dailyApi';
import {
  DAILY_DEFAULT_ZOOM_ORIGIN,
  DAILY_ERROR_DISPLAY_MS,
  DAILY_GUESS_ROWS,
  DAILY_INTERPOLATION_INITIAL_ZOOM,
  DAILY_MIN_ZOOM,
  DAILY_ZOOM_BOUNDS,
  DAILY_WIN_MODAL_DELAY_MS,
} from '../../../constants/games/dailyGame';
import {
  buildCompletedDailyAttempt,
  buildInProgressDailyAttempt,
  createEmptyDailyStats,
  normalizeDailyAttempt,
} from '../../../utils/games/dailyGameAttemptUtils';
import {
  ensureGuestDailyAttempt,
  saveGuestDailyAttempt,
} from '../../../utils/games/dailyGameStorage';
import { normalizeDailyZoomOrigin } from '../../../utils/games/dailyZoomConfig';
import {
  ensureDailyAttemptDb,
  getDailyAttemptDb,
  getDailyStatsDb,
  saveDailyAttemptDb,
} from '../../../utils/games/dailyGameStorageDb';
import {
  loadDailyHowToDismissedPreference,
  saveDailyHowToDismissedPreference,
} from '../../../utils/games/dailyHowToPreference';
import { useDailyRoundTracking } from '../../../utils/hooks/useDailyRoundTracking';
import '../../../styles/gamePolish.css';
import './ZoomedInDailyGame.css';

const emptyGuesses = () => Array(DAILY_GUESS_ROWS).fill('');
const DAILY_ZOOM_TRANSITION = 'all 1.15s cubic-bezier(0.22, 1, 0.36, 1)';
const DAILY_START_ONLY_ZOOM_MULTIPLIER = 1.2;
const DAILY_START_ZOOM_LEVEL = Math.round(
  DAILY_INTERPOLATION_INITIAL_ZOOM * DAILY_START_ONLY_ZOOM_MULTIPLIER,
);
const DAILY_ZOOM_LEVELS_BY_ROW = Object.freeze([
  DAILY_START_ZOOM_LEVEL,
  480,
  240,
  160,
  100,
  100,
]);
const DAILY_FULLY_ZOOMED_OUT_ROW_INDEX = 4;
const DAILY_ZOOM_HINT_DISPLAY_MS = 2200;
const DAILY_ZOOM_HINT_FADE_MS = 500;
const DAILY_START_HINT_DISPLAY_MS = 3400;
const DAILY_START_REMINDER_TEXT =
  'Remember, sometimes the clues help...sometimes it misleads';
const DAILY_START_HINT_TEXT = 'Keep guessing to zoom out!';
const DAILY_FULLY_ZOOMED_HINT_TEXT = 'The image is now fully zoomed out. You got this!';

const getZoomLevelForRow = (rowIndex) => {
  const index = Number.isFinite(Number(rowIndex)) ? Math.max(0, Math.floor(Number(rowIndex))) : 0;
  return (
    DAILY_ZOOM_LEVELS_BY_ROW[index] ??
    DAILY_ZOOM_LEVELS_BY_ROW[DAILY_ZOOM_LEVELS_BY_ROW.length - 1] ??
    DAILY_MIN_ZOOM
  );
};

const buildInitialZoomLevel = () => getZoomLevelForRow(0);

const buildFreshState = (puzzle) => {
  const initialZoom = buildInitialZoomLevel();
  return {
    guesses: emptyGuesses(),
    currentGuess: '',
    currentRow: 0,
    gameOver: false,
    zoomLevel: initialZoom,
    zoomOrigin: normalizeDailyZoomOrigin(puzzle?.zoomOrigin, DAILY_DEFAULT_ZOOM_ORIGIN),
  };
};

const normalizeWordList = (text) =>
  text
    .split('\n')
    .map((word) => word.trim().toLowerCase())
    .filter((word) => /^[a-z]{5}$/.test(word));

const updateUsedLetters = (previous, guess, solution) => {
  const result = new Set(previous);
  const guessUpper = guess.toUpperCase();
  const solutionUpper = solution.toUpperCase();

  for (let i = 0; i < guessUpper.length; i += 1) {
    const letter = guessUpper[i];
    if (!letter) continue;
    if (solutionUpper[i] === letter) {
      result.delete(`${letter}-present`);
      result.delete(`${letter}-absent`);
      result.add(`${letter}-correct`);
      continue;
    }
    if (solutionUpper.includes(letter) && !result.has(`${letter}-correct`)) {
      result.delete(`${letter}-absent`);
      result.add(`${letter}-present`);
      continue;
    }
    if (!result.has(`${letter}-correct`) && !result.has(`${letter}-present`)) {
      result.add(`${letter}-absent`);
    }
  }

  return result;
};

const deriveUsedLettersFromGuesses = (guesses, solution) => {
  if (!solution) return new Set();
  const normalizedSolution = String(solution).toLowerCase();
  const guessRows = Array.isArray(guesses) ? guesses : [];
  return guessRows.reduce((accumulator, rowGuess) => {
    const guess = String(rowGuess || '').trim().toLowerCase();
    if (!guess) return accumulator;
    return updateUsedLetters(accumulator, guess, normalizedSolution);
  }, new Set());
};

const ZoomedInDailyGame = ({ mobileSideBySide = false, openHowToRequest = 0 }) => {
  const navigate = useNavigate();
  const {
    user,
    loading: profileLoading,
    setSeasonPoints,
    setLifetimePoints,
  } = useProfile();
  const [dailyPuzzle, setDailyPuzzle] = useState(null);
  const [gameState, setGameState] = useState(buildFreshState(null));
  const [usedLetters, setUsedLetters] = useState(new Set());
  const [dictionary, setDictionary] = useState(new Set());
  const [dictionaryLoaded, setDictionaryLoaded] = useState(false);
  const [loadingPuzzle, setLoadingPuzzle] = useState(true);
  const [isHydratingAttempt, setIsHydratingAttempt] = useState(true);
  const [imageLoading, setImageLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [secondsUntilRollover, setSecondsUntilRollover] = useState(null);
  const [playerStats, setPlayerStats] = useState(() => createEmptyDailyStats());
  const [showHowToPlayModal, setShowHowToPlayModal] = useState(() => {
    const dismissedPreference = loadDailyHowToDismissedPreference();
    return dismissedPreference === null ? true : !dismissedPreference;
  });
  const [showDefinition, setShowDefinition] = useState(false);
  const [definition, setDefinition] = useState(null);
  const [isLoadingDefinition, setIsLoadingDefinition] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [isRiddleCollapsed, setIsRiddleCollapsed] = useState(false);
  const [zoomHint, setZoomHint] = useState({ text: '', fading: false, variant: 'default' });
  const [modalState, setModalState] = useState({
    show: false,
    title: '',
    isWin: false,
    pointsAwarded: 0,
    firstGameBonus: false,
  });
  const activePuzzleDateRef = useRef('');
  const persistScopeRef = useRef('');
  const zoomHintTimersRef = useRef({ fade: null, clear: null });
  const shownZoomHintsRef = useRef({
    scopeKey: '',
    startHintShown: false,
    fullyZoomedHintShown: false,
  });

  const clearZoomHintTimers = useCallback(() => {
    if (zoomHintTimersRef.current.fade) {
      window.clearTimeout(zoomHintTimersRef.current.fade);
      zoomHintTimersRef.current.fade = null;
    }
    if (zoomHintTimersRef.current.clear) {
      window.clearTimeout(zoomHintTimersRef.current.clear);
      zoomHintTimersRef.current.clear = null;
    }
  }, []);

  const hideZoomHint = useCallback(() => {
    clearZoomHintTimers();
    setZoomHint({ text: '', fading: false, variant: 'default' });
  }, [clearZoomHintTimers]);

  const showZoomHint = useCallback(
    (text, displayMs = DAILY_ZOOM_HINT_DISPLAY_MS, variant = 'default') => {
      if (!text) return;
      clearZoomHintTimers();
      setZoomHint({ text, fading: false, variant });
      zoomHintTimersRef.current.fade = window.setTimeout(() => {
        setZoomHint((previous) => (
          previous.text ? { ...previous, fading: true } : previous
        ));
      }, displayMs);
      zoomHintTimersRef.current.clear = window.setTimeout(() => {
        setZoomHint({ text: '', fading: false, variant: 'default' });
        zoomHintTimersRef.current.fade = null;
        zoomHintTimersRef.current.clear = null;
      }, displayMs + DAILY_ZOOM_HINT_FADE_MS);
    },
    [clearZoomHintTimers],
  );

  const showError = useCallback((message) => {
    setErrorMessage(message);
    window.setTimeout(() => {
      setErrorMessage('');
    }, DAILY_ERROR_DISPLAY_MS);
  }, []);

  const closeHowToPlayModal = useCallback(() => {
    setShowHowToPlayModal(false);
    saveDailyHowToDismissedPreference(true);
  }, []);

  const openImagePreview = useCallback((clickedSrc) => {
    if (!clickedSrc) return;
    setPreviewImageUrl(clickedSrc);
  }, []);

  const closeImagePreview = useCallback(() => {
    setPreviewImageUrl('');
  }, []);

  const refreshPlayerStats = useCallback(async () => {
    if (!user?.id) {
      setPlayerStats(createEmptyDailyStats());
      return;
    }
    const nextStats = await getDailyStatsDb({ userId: user.id });
    setPlayerStats(nextStats);
  }, [user?.id]);

  const handleDailyRoundTrackingFinish = useCallback((result) => {
    const awardedPoints = Number(result?.pointsAwarded) || 0;
    if (awardedPoints <= 0) return;

    setModalState((previous) => ({
      ...previous,
      pointsAwarded: awardedPoints,
      firstGameBonus: Boolean(result?.firstGameBonus),
    }));

    if (typeof setSeasonPoints === 'function') {
      setSeasonPoints((previous) => (previous ?? 0) + awardedPoints);
    }
    if (typeof setLifetimePoints === 'function') {
      setLifetimePoints((previous) => (previous ?? 0) + awardedPoints);
    }
  }, [setLifetimePoints, setSeasonPoints]);

  const {
    beginTracking: beginDailyRoundTracking,
    finishTracking: finishDailyRoundTracking,
  } = useDailyRoundTracking({
    userId: user?.id || null,
    onFinish: handleDailyRoundTrackingFinish,
  });

  const {
    debounced: persistInProgressAttemptDebounced,
    cancel: cancelPersistInProgressAttemptDebounced,
  } = useDebouncedCallback(
    async ({ userId, puzzleDateUtc, attempt, scopeKey }) => {
      if (!puzzleDateUtc) return;
      if (scopeKey !== persistScopeRef.current) return;
      if (userId) {
        await saveDailyAttemptDb({ userId, puzzleDateUtc, attempt });
        return;
      }
      saveGuestDailyAttempt({ puzzleDateUtc, attempt });
    },
    550,
  );

  const resetBoardForPuzzle = useCallback((puzzle) => {
    setGameState(buildFreshState(puzzle));
    setUsedLetters(new Set());
    setModalState({
      show: false,
      title: '',
      isWin: false,
      pointsAwarded: 0,
      firstGameBonus: false,
    });
    setShowConfetti(false);
    hideZoomHint();
    setIsHydratingAttempt(true);
    setImageLoading(true);
    setDefinition(null);
    setShowDefinition(false);
    setIsLoadingDefinition(false);
    setIsRiddleCollapsed(false);
  }, [hideZoomHint]);

  const loadPuzzle = useCallback(async ({ forceReset = false } = {}) => {
    setLoadingPuzzle(true);
    try {
      const puzzle = await fetchDailyPuzzle();
      setDailyPuzzle(puzzle);
      setSecondsUntilRollover(
        Number.isFinite(Number(puzzle?.secondsUntilRollover))
          ? Number(puzzle.secondsUntilRollover)
          : null,
      );

      const dateChanged = activePuzzleDateRef.current !== puzzle?.dateUtc;
      if (dateChanged || forceReset) {
        resetBoardForPuzzle(puzzle);
      }
      activePuzzleDateRef.current = puzzle?.dateUtc || '';
    } catch (error) {
      showError(
        error?.code === 'DAILY_NOT_READY'
          ? 'Daily puzzle is not ready yet. Try again soon.'
          : error?.message || 'Failed to load daily puzzle.',
      );
    } finally {
      setLoadingPuzzle(false);
    }
  }, [resetBoardForPuzzle, showError]);

  const hydrateAttemptState = useCallback(
    async (puzzle) => {
      if (!puzzle?.dateUtc || !puzzle?.word) return;

      const baseState = buildFreshState(puzzle);
      const scopeKey = `${user?.id || 'guest'}:${puzzle.dateUtc}`;
      persistScopeRef.current = scopeKey;
      cancelPersistInProgressAttemptDebounced();
      setIsHydratingAttempt(true);

      try {
        let restoredAttempt = null;
        if (user?.id) {
          restoredAttempt = await ensureDailyAttemptDb({
            userId: user.id,
            puzzleDateUtc: puzzle.dateUtc,
            gameState: baseState,
          });
        } else {
          restoredAttempt = ensureGuestDailyAttempt({
            puzzleDateUtc: puzzle.dateUtc,
            gameState: baseState,
          });
        }

        if (persistScopeRef.current !== scopeKey) return;

        const normalizedAttempt = normalizeDailyAttempt(
          restoredAttempt || buildInProgressDailyAttempt(baseState),
        );
        const restoredZoomLevel = normalizedAttempt.gameOver && !normalizedAttempt.isWin
          ? DAILY_MIN_ZOOM
          : getZoomLevelForRow(normalizedAttempt.currentRow);

        setGameState({
          ...baseState,
          guesses: normalizedAttempt.guesses,
          currentGuess: normalizedAttempt.currentGuess,
          currentRow: normalizedAttempt.currentRow,
          gameOver: normalizedAttempt.gameOver,
          zoomLevel: restoredZoomLevel,
        });
        setUsedLetters(
          deriveUsedLettersFromGuesses(normalizedAttempt.guesses, puzzle.word),
        );
        setShowConfetti(false);
        setModalState(
          normalizedAttempt.gameOver
            ? {
                show: true,
                title: normalizedAttempt.isWin
                  ? "You solved today's Daily!"
                  : `Daily over. Word was ${String(puzzle.word).toUpperCase()}`,
                isWin: normalizedAttempt.isWin,
                pointsAwarded: 0,
                firstGameBonus: false,
              }
            : {
                show: false,
                title: '',
                isWin: false,
                pointsAwarded: 0,
                firstGameBonus: false,
              },
        );
      } finally {
        if (persistScopeRef.current === scopeKey) {
          setIsHydratingAttempt(false);
        }
      }
    },
    [cancelPersistInProgressAttemptDebounced, user?.id],
  );

  const persistCompletedAttempt = useCallback(
    async ({ guesses, currentRow, isWin, guessesUsed }) => {
      if (!dailyPuzzle?.dateUtc) return;

      const completedAttempt = buildCompletedDailyAttempt({
        guesses,
        currentRow,
        isWin,
        guessesUsed,
      });

      cancelPersistInProgressAttemptDebounced();

      if (user?.id) {
        const didSave = await saveDailyAttemptDb({
          userId: user.id,
          puzzleDateUtc: dailyPuzzle.dateUtc,
          attempt: completedAttempt,
        });
        if (!didSave) {
          await hydrateAttemptState(dailyPuzzle);
          showError('Daily already completed on another device.');
          return;
        }
        await finishDailyRoundTracking({
          puzzleDateUtc: dailyPuzzle.dateUtc,
          won: isWin,
          guessesUsed: isWin ? guessesUsed : DAILY_GUESS_ROWS,
        });
        await refreshPlayerStats();
        return;
      }

      saveGuestDailyAttempt({
        puzzleDateUtc: dailyPuzzle.dateUtc,
        attempt: completedAttempt,
      });
    },
    [
      cancelPersistInProgressAttemptDebounced,
      dailyPuzzle,
      hydrateAttemptState,
      refreshPlayerStats,
      showError,
      user?.id,
      finishDailyRoundTracking,
    ],
  );

  useEffect(() => {
    if (profileLoading) return;
    refreshPlayerStats();
  }, [profileLoading, refreshPlayerStats]);

  useEffect(() => {
    if (loadingPuzzle || !dailyPuzzle?.dateUtc) return;
    if (profileLoading) return;
    hydrateAttemptState(dailyPuzzle);
  }, [
    dailyPuzzle,
    dailyPuzzle?.dateUtc,
    hydrateAttemptState,
    loadingPuzzle,
    profileLoading,
  ]);

  useEffect(() => {
    if (!user?.id) return;
    if (!dailyPuzzle?.dateUtc) return;
    if (loadingPuzzle || profileLoading || isHydratingAttempt) return;
    if (gameState.gameOver) return;
    void beginDailyRoundTracking({ puzzleDateUtc: dailyPuzzle.dateUtc });
  }, [
    beginDailyRoundTracking,
    dailyPuzzle?.dateUtc,
    gameState.gameOver,
    isHydratingAttempt,
    loadingPuzzle,
    profileLoading,
    user?.id,
  ]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const response = await fetch('/wordlistdictionary.txt');
        if (!response.ok) {
          throw new Error('Failed to load dictionary');
        }
        const text = await response.text();
        if (!mounted) return;
        setDictionary(new Set(normalizeWordList(text)));
        setDictionaryLoaded(true);
      } catch (error) {
        console.error('[daily] dictionary load failed:', error);
        if (mounted) {
          showError('Dictionary failed to load. Word validation may be limited.');
          setDictionaryLoaded(true);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [showError]);

  useEffect(() => {
    loadPuzzle();
  }, [loadPuzzle]);

  useEffect(() => {
    if (Number(openHowToRequest) > 0) {
      setShowHowToPlayModal(true);
    }
  }, [openHowToRequest]);

  useEffect(() => {
    setPreviewImageUrl('');
  }, [dailyPuzzle?.imageUrl]);

  useEffect(() => {
    if (!Number.isFinite(secondsUntilRollover)) return undefined;
    if (secondsUntilRollover <= 0) return undefined;
    const id = window.setInterval(() => {
      setSecondsUntilRollover((previous) =>
        Number.isFinite(previous) ? Math.max(0, previous - 1) : previous,
      );
    }, 1000);
    return () => window.clearInterval(id);
  }, [secondsUntilRollover]);

  useEffect(() => {
    if (!Number.isFinite(secondsUntilRollover)) return;
    if (secondsUntilRollover !== 0) return;
    loadPuzzle({ forceReset: true });
  }, [secondsUntilRollover, loadPuzzle]);

  useEffect(() => {
    if (!showConfetti) return undefined;
    const timeoutId = window.setTimeout(() => {
      setShowConfetti(false);
    }, 2200);
    return () => window.clearTimeout(timeoutId);
  }, [showConfetti]);

  useEffect(() => () => {
    clearZoomHintTimers();
  }, [clearZoomHintTimers]);

  useEffect(() => {
    const scopeKey = `${String(user?.id || 'guest')}:${String(dailyPuzzle?.dateUtc || '')}`;
    if (!scopeKey) return;
    if (shownZoomHintsRef.current.scopeKey === scopeKey) return;

    shownZoomHintsRef.current = {
      scopeKey,
      startHintShown: false,
      fullyZoomedHintShown: false,
    };
    hideZoomHint();
  }, [dailyPuzzle?.dateUtc, hideZoomHint, user?.id]);

  useEffect(() => {
    if (!dailyPuzzle?.dateUtc) return;
    if (loadingPuzzle || profileLoading || isHydratingAttempt || imageLoading) return;
    if (gameState.gameOver || modalState.show) {
      hideZoomHint();
      return;
    }

    if (gameState.currentRow === 0 && !shownZoomHintsRef.current.startHintShown) {
      shownZoomHintsRef.current.startHintShown = true;
      showZoomHint(DAILY_START_HINT_TEXT, DAILY_START_HINT_DISPLAY_MS, 'start');
      return;
    }

    if (
      gameState.currentRow >= DAILY_FULLY_ZOOMED_OUT_ROW_INDEX &&
      !shownZoomHintsRef.current.fullyZoomedHintShown
    ) {
      shownZoomHintsRef.current.fullyZoomedHintShown = true;
      showZoomHint(DAILY_FULLY_ZOOMED_HINT_TEXT);
    }
  }, [
    dailyPuzzle?.dateUtc,
    gameState.currentRow,
    gameState.gameOver,
    hideZoomHint,
    imageLoading,
    isHydratingAttempt,
    loadingPuzzle,
    modalState.show,
    profileLoading,
    showZoomHint,
  ]);

  useEffect(() => {
    if (loadingPuzzle || !dailyPuzzle?.dateUtc) return;
    if (profileLoading || isHydratingAttempt) return;
    if (gameState.gameOver) {
      cancelPersistInProgressAttemptDebounced();
      return;
    }

    const scopeKey = `${user?.id || 'guest'}:${dailyPuzzle.dateUtc}`;
    persistInProgressAttemptDebounced({
      userId: user?.id || null,
      puzzleDateUtc: dailyPuzzle.dateUtc,
      attempt: buildInProgressDailyAttempt(gameState),
      scopeKey,
    });
  }, [
    cancelPersistInProgressAttemptDebounced,
    dailyPuzzle?.dateUtc,
    gameState,
    isHydratingAttempt,
    loadingPuzzle,
    persistInProgressAttemptDebounced,
    profileLoading,
    user?.id,
  ]);

  const fetchDefinition = useCallback(async (word) => {
    if (!word) return;
    setIsLoadingDefinition(true);
    try {
      const response = await fetch(
        `https://api.dictionaryapi.dev/api/v2/entries/en/${String(word).toLowerCase()}`,
      );
      if (!response.ok) throw new Error('Definition not found');
      const data = await response.json();
      setDefinition(data);
    } catch (error) {
      console.error('Error fetching definition:', error);
      setDefinition(null);
    } finally {
      setIsLoadingDefinition(false);
    }
  }, []);

  const toggleDefinition = useCallback(() => {
    if (!dailyPuzzle?.word) return;
    if (!showDefinition) {
      fetchDefinition(dailyPuzzle.word);
    }
    setShowDefinition((previous) => !previous);
  }, [dailyPuzzle?.word, fetchDefinition, showDefinition]);

  const evaluateGuess = useCallback(
    (guess) => {
      if (!dailyPuzzle?.word) return;
      const normalizedGuess = guess.toLowerCase();
      const solution = dailyPuzzle.word.toLowerCase();
      const rowIndex = gameState.currentRow;
      const isWin = normalizedGuess === solution;
      const isLastRow = rowIndex >= DAILY_GUESS_ROWS - 1;
      const nextGuesses = [...gameState.guesses];
      nextGuesses[rowIndex] = normalizedGuess.toUpperCase();

      setUsedLetters((previous) => updateUsedLetters(previous, normalizedGuess, solution));
      if (isWin || isLastRow) {
        const guessesUsed = rowIndex + 1;
        setGameState((previous) => ({
          ...previous,
          guesses: nextGuesses,
          currentGuess: '',
          gameOver: true,
          zoomLevel: isWin ? previous.zoomLevel : DAILY_MIN_ZOOM,
        }));

        if (isWin) {
          window.setTimeout(() => {
            setShowConfetti(true);
            setModalState({
              show: true,
              title: "You solved today's Daily!",
              isWin: true,
              pointsAwarded: 0,
              firstGameBonus: false,
            });
          }, DAILY_WIN_MODAL_DELAY_MS);
        } else {
          setModalState({
            show: true,
            title: `Daily over. Word was ${dailyPuzzle.word.toUpperCase()}`,
            isWin: false,
            pointsAwarded: 0,
            firstGameBonus: false,
          });
        }

        void persistCompletedAttempt({
          guesses: nextGuesses,
          currentRow: rowIndex,
          isWin,
          guessesUsed: isWin ? guessesUsed : DAILY_GUESS_ROWS,
        });
        return;
      }

      setGameState((previous) => ({
        ...previous,
        guesses: nextGuesses,
        currentGuess: '',
        currentRow: rowIndex + 1,
        zoomLevel: getZoomLevelForRow(rowIndex + 1),
        gameOver: false,
      }));
    },
    [dailyPuzzle, gameState.currentRow, gameState.guesses, persistCompletedAttempt],
  );

  const submitCurrentGuess = useCallback(async () => {
    if (!dailyPuzzle?.word) return;
    if (isHydratingAttempt) return;
    if (gameState.gameOver) return;
    if (gameState.currentGuess.length !== 5) {
      showError('Please enter a 5-letter word.');
      return;
    }

    const guessLower = gameState.currentGuess.toLowerCase();
    if (dictionaryLoaded && dictionary.size > 0 && !dictionary.has(guessLower)) {
      showError('Word not in dictionary.');
      return;
    }

    // Cross-device guard: if this user finished today's daily elsewhere,
    // lock immediately before processing any new guess on this device.
    if (user?.id && dailyPuzzle?.dateUtc) {
      const latestAttempt = await getDailyAttemptDb({
        userId: user.id,
        puzzleDateUtc: dailyPuzzle.dateUtc,
      });
      if (latestAttempt?.gameOver) {
        await hydrateAttemptState(dailyPuzzle);
        showError('Daily already completed on another device.');
        return;
      }
    }

    evaluateGuess(guessLower);
  }, [
    dailyPuzzle,
    dictionary,
    dictionaryLoaded,
    evaluateGuess,
    gameState.currentGuess,
    gameState.gameOver,
    hydrateAttemptState,
    isHydratingAttempt,
    showError,
    user?.id,
  ]);

  const handleKeyPress = useCallback(
    (key) => {
      if (isHydratingAttempt) return;
      if (previewImageUrl) return;
      if (gameState.gameOver) return;
      if (!dailyPuzzle?.word) return;

      if (key === 'ENTER') {
        submitCurrentGuess();
        return;
      }

      if (key === '⌫' || key === 'Backspace') {
        setGameState((previous) => ({
          ...previous,
          currentGuess: previous.currentGuess.slice(0, -1),
        }));
        return;
      }

      if (!/^[A-Z]$/.test(key)) return;

      setGameState((previous) => {
        if (previous.currentGuess.length >= 5) return previous;
        return {
          ...previous,
          currentGuess: previous.currentGuess + key,
        };
      });
    },
    [dailyPuzzle, gameState.gameOver, isHydratingAttempt, previewImageUrl, submitCurrentGuess],
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      if (previewImageUrl) return;
      if (modalState.show) return;
      if (showHowToPlayModal) return;
      if (loadingPuzzle) return;
      if (profileLoading || isHydratingAttempt) return;
      if (event.key === 'Enter') {
        event.preventDefault();
        handleKeyPress('ENTER');
        return;
      }
      if (event.key === 'Backspace') {
        handleKeyPress('Backspace');
        return;
      }
      const letter = event.key.toUpperCase();
      if (/^[A-Z]$/.test(letter)) {
        handleKeyPress(letter);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    }, [
    handleKeyPress,
    isHydratingAttempt,
    loadingPuzzle,
    modalState.show,
    previewImageUrl,
    profileLoading,
    showHowToPlayModal,
  ]);

  const shouldShowDailyRiddle =
    !loadingPuzzle &&
    !profileLoading &&
    !isHydratingAttempt &&
    Boolean(dailyPuzzle?.riddleText) &&
    gameState.currentRow >= 3;

  const shareZoomOrigin = useMemo(
    () => normalizeDailyZoomOrigin(dailyPuzzle?.zoomOrigin ?? gameState.zoomOrigin),
    [dailyPuzzle?.zoomOrigin, gameState.zoomOrigin],
  );

  return (
    <div
      className={`game zi-game-shell daily-game ${
        mobileSideBySide ? 'daily-game--split' : ''
      }`.trim()}
      style={{ '--daily-guess-rows': DAILY_GUESS_ROWS }}
    >
      {showConfetti && <Confetti recycle={false} numberOfPieces={170} />}

      {modalState.show && (
        <DailyGameModal
          title={modalState.title}
          isWin={modalState.isWin}
          user={user}
          stats={playerStats}
          solution={dailyPuzzle?.word || ''}
          imageUrl={dailyPuzzle?.imageUrl || ''}
          puzzleDateUtc={dailyPuzzle?.dateUtc || ''}
          shareZoomLevel={buildInitialZoomLevel()}
          shareZoomOrigin={shareZoomOrigin}
          guesses={gameState.guesses}
          secondsUntilRollover={secondsUntilRollover}
          showDefinition={showDefinition}
          definition={definition}
          isLoadingDefinition={isLoadingDefinition}
          toggleDefinition={toggleDefinition}
          pointsAwarded={modalState.pointsAwarded ?? 0}
          firstGameBonus={modalState.firstGameBonus ?? false}
          onHome={() => navigate('/')}
        />
      )}

      {showHowToPlayModal && <DailyHowToPlayModal onClose={closeHowToPlayModal} />}

      {previewImageUrl && (
        <ImagePreviewModal
          imageUrl={previewImageUrl}
          zoomLevel={gameState.zoomLevel}
          zoomOrigin={gameState.zoomOrigin}
          zoomBounds={DAILY_ZOOM_BOUNDS}
          disableImageReveal
          alt={
            dailyPuzzle?.word
              ? `Expanded generated image for ${dailyPuzzle.word.toUpperCase()}`
              : 'Expanded generated image'
          }
          onClose={closeImagePreview}
        />
      )}

      {errorMessage && (
        <div className="error-message" role="alert">
          {errorMessage}
        </div>
      )}

      {loadingPuzzle || profileLoading || isHydratingAttempt || !dailyPuzzle ? (
        <div className="loading-spinner">
          <div className="spinner" />
          <span className="loading-text">Loading daily puzzle...</span>
        </div>
      ) : (
        <>
          <div className="game-top-container">
            <div
              className={`daily-top-main ${
                mobileSideBySide ? 'daily-top-main--sideBySide' : ''
              }`.trim()}
            >
              <GameImage
                imageLoading={imageLoading}
                imageUrl={dailyPuzzle.imageUrl}
                zoomLevel={gameState.zoomLevel}
                zoomOrigin={gameState.zoomOrigin}
                zoomBounds={DAILY_ZOOM_BOUNDS}
                disableImageReveal
                isChecking={false}
                disableTransition={false}
                waitingForFallback={false}
                solution={dailyPuzzle.word}
                transitionOverride={DAILY_ZOOM_TRANSITION}
                onLoad={() => setImageLoading(false)}
                onError={() => setImageLoading(false)}
                onImageClick={
                  imageLoading ||
                  loadingPuzzle ||
                  profileLoading ||
                  isHydratingAttempt ||
                  modalState.show ||
                  showHowToPlayModal
                    ? undefined
                    : openImagePreview
                }
              >
                {zoomHint.text ? (
                  <div
                    className="daily-zoom-hint-stack"
                    role="status"
                    aria-live="polite"
                  >
                    {zoomHint.variant === 'start' ? (
                      <div
                        className={`daily-zoom-hint ${zoomHint.fading ? 'daily-zoom-hint--fade' : ''}`}
                      >
                        {DAILY_START_REMINDER_TEXT}
                      </div>
                    ) : null}
                    <div className={`daily-zoom-hint ${zoomHint.fading ? 'daily-zoom-hint--fade' : ''}`}>
                      {zoomHint.text}
                    </div>
                  </div>
                ) : null}
              </GameImage>

              {shouldShowDailyRiddle ? (
                <div className="daily-riddle-slot daily-riddle-slot--stack">
                  <DailyRiddleHint
                    text={dailyPuzzle?.riddleText}
                    isCollapsed={isRiddleCollapsed}
                    onToggleCollapse={() => setIsRiddleCollapsed((previous) => !previous)}
                  />
                </div>
              ) : null}

              <Grid
                key={`daily-${dailyPuzzle.dateUtc || 'pending'}-${
                  mobileSideBySide ? 'split' : 'stack'
                }`}
                guesses={gameState.guesses}
                currentGuess={gameState.currentGuess}
                currentRow={gameState.currentRow}
                solution={dailyPuzzle.word}
                isTypingEnabled={!gameState.gameOver && !isHydratingAttempt && !showHowToPlayModal}
              />
            </div>
          </div>

          {shouldShowDailyRiddle ? (
            <div className="daily-riddle-slot daily-riddle-slot--main">
              <DailyRiddleHint
                text={dailyPuzzle?.riddleText}
                isCollapsed={isRiddleCollapsed}
                onToggleCollapse={() => setIsRiddleCollapsed((previous) => !previous)}
              />
            </div>
          ) : null}

          <div className="game-bottom-container">
            <Keyboard onKeyPress={handleKeyPress} usedLetters={usedLetters} />
          </div>

          <div className="daily-game__countdown-wrap">
            <DailyCountdown secondsUntilRollover={secondsUntilRollover} />
          </div>
        </>
      )}
    </div>
  );
};

export default ZoomedInDailyGame;
